import { useEffect, useState } from 'react'
import { Analytics } from '@vercel/analytics/react'
import { menuProcessor } from './services/menuProcessor'
import { Menu } from './types/menu'
import { useIsMobile } from './hooks/useIsMobile'
import { useRotatingMessage } from './hooks/useRotatingMessage'
import { Footer } from './components/layout/Footer'
import { FeedbackModal } from './components/feedback/FeedbackModal'
import { MobileNotice } from './components/screens/MobileNotice'
import { LandingScreen } from './components/screens/LandingScreen'
import { LoadingScreen } from './components/screens/LoadingScreen'
import { EditorScreen } from './components/screens/EditorScreen'
import { clearDraft, loadDraft, saveDraft } from './utils/menuDraft'

type Screen = 'landing' | 'loading' | 'editing'

const LOADING_MESSAGES = [
  'Setting the table…',
  'Preparing your menu…',
  'Organizing your spread…',
  'Getting things menu-ready…',
] as const

function App() {
  const isMobile = useIsMobile()
  const [screen, setScreen] = useState<Screen>('landing')
  const [files, setFiles] = useState<File[]>([])
  const [menu, setMenu] = useState<Menu | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [abortController, setAbortController] = useState<AbortController | null>(null)
  const [showFeedback, setShowFeedback] = useState(false)
  const [warnings, setWarnings] = useState<string[]>([])
  const [draft, setDraft] = useState(loadDraft)
  const loadingMessage = useRotatingMessage(LOADING_MESSAGES, isProcessing)

  // Autosave the menu while editing so a refresh doesn't lose work
  useEffect(() => {
    if (screen === 'editing' && menu) saveDraft(menu)
  }, [screen, menu])

  const discardDraft = () => {
    clearDraft()
    setDraft(null)
  }

  const resumeDraft = () => {
    if (!draft) return
    setMenu(draft.menu)
    setWarnings([])
    setDraft(null)
    setScreen('editing')
  }

  const resetToLanding = () => {
    discardDraft()
    setScreen('landing')
    setFiles([])
    setMenu(null)
    setWarnings([])
    setError(null)
  }

  const addFiles = (newFiles: File[]) => {
    setFiles((prev) => [...prev, ...newFiles])
    setScreen('loading')
    setError(null)
    setMenu(null)
  }

  const removeFile = (fileToRemove: File) => {
    // Removing a file while processing cancels the run
    if (isProcessing && abortController) {
      abortController.abort()
      setAbortController(null)
      setIsProcessing(false)
    }

    const remaining = files.filter((file) => file !== fileToRemove)
    if (remaining.length === 0) {
      resetToLanding()
    } else {
      setFiles(remaining)
    }
  }

  const convert = async () => {
    if (files.length === 0) return

    const controller = new AbortController()
    setAbortController(controller)
    setIsProcessing(true)

    try {
      const result = await menuProcessor.processMultipleMenuFiles(files, controller.signal)
      if (controller.signal.aborted) return

      console.log('Menu processed:', {
        files: result.processedFiles,
        sections: result.consolidatedSections,
        confidence: result.confidence,
        tokensUsed: result.tokensUsed,
        warnings: result.warnings,
      })

      setMenu(result.menu)
      setWarnings(result.warnings)
      setDraft(null)
      setScreen('editing')
    } catch (err) {
      if (controller.signal.aborted) return
      console.error('Processing error:', err)
      setError(err instanceof Error ? err.message : 'Failed to process files')
      setScreen('landing')
    } finally {
      setIsProcessing(false)
      setAbortController(null)
    }
  }

  const renderScreen = () => {
    if (screen === 'editing' && menu) {
      return (
        <EditorScreen
          menu={menu}
          onMenuChange={setMenu}
          onStartNewMenu={resetToLanding}
          warnings={warnings}
          onDismissWarnings={() => setWarnings([])}
        />
      )
    }
    if (screen === 'loading') {
      return (
        <LoadingScreen
          files={files}
          isProcessing={isProcessing}
          loadingMessage={loadingMessage}
          onRemoveFile={removeFile}
          onConvert={convert}
        />
      )
    }
    return (
      <LandingScreen
        onFilesAdded={addFiles}
        error={error}
        draft={draft}
        onResumeDraft={resumeDraft}
        onDiscardDraft={discardDraft}
      />
    )
  }

  return (
    <>
      {isMobile ? (
        <MobileNotice />
      ) : (
        <div className="relative">
          {renderScreen()}
          <Footer onFeedbackClick={() => setShowFeedback(true)} />
          <FeedbackModal isOpen={showFeedback} onClose={() => setShowFeedback(false)} />
        </div>
      )}
      <Analytics />
    </>
  )
}

export default App
